import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import keycloak from '../utils/keycloak';
import { setToken } from '../utils/auth';
import './Auth.css';
import CarSizeOptions from './CarSizeOptions';
import PasswordField from './PasswordField';
import PasswordChecklist from './PasswordChecklist';
import { checkPassword, passwordProblem } from '../utils/passwordPolicy';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [carColor, setCarColor] = useState('');
  const [carType, setCarType] = useState('');
  const [showCarDetailsFields, setShowCarDetailsFields] = useState(false);
  // 2026-09-26: a Google sign-up now chooses its own username instead of silently receiving one
  // derived from the Google profile. Only a BRAND-NEW account is asked — the server says which case
  // this is, because an existing account with incomplete car details already has a username.
  const [googleUsername, setGoogleUsername] = useState('');
  const [needsUsername, setNeedsUsername] = useState(false);
  const [tempIdToken, setTempIdToken] = useState(null);
  // Forgot password (2026-10-26, the same flow as the mobile app's — server/utils/passwordReset.js):
  // null while signing in, 'email' while asking for the address, 'code' for the code + new password.
  const [resetStep, setResetStep] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPassword2, setResetPassword2] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState('');
  // Shown above the sign-in form once the password has been changed.
  const [notice, setNotice] = useState('');
  const navigate = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    const idToken = credentialResponse.credential;
    try {
      const response = await fetch('/api/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          idToken,
          plateNumber: plateNumber || null,
          carColor: carColor || null,
          carType: carType || null,
          username: googleUsername || null
        }),
      });

      // The server answers JSON on every one of these paths now, but a body that isn't JSON must
      // not turn a useful error into an unhandled exception — fall back to the raw text.
      const readBody = async (r) => {
        const raw = await r.text();
        try { return JSON.parse(raw); } catch { return { message: raw }; }
      };

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.token);
        sessionStorage.setItem('welcomeMessage', `Welcome, ${data.username}!`);
        navigate('/dashboard');
      } else if (response.status === 428) { // Precondition Required - missing car details
        const data = await readBody(response);
        setTempIdToken(idToken);
        setShowCarDetailsFields(true);
        setNeedsUsername(!!data.needsUsername);
        // Pre-filled, so the common case is a single click and only someone who cares has to type.
        if (data.suggestedUsername) setGoogleUsername(data.suggestedUsername);
        alert(data.needsUsername
          ? 'Please confirm your username and car details to finish signing up.'
          : 'Please provide your car details to complete Google login.');
      } else {
        const data = await readBody(response);
        // A rejected username keeps the form open — the user is being asked to change one field,
        // not to start the whole sign-in again.
        if (data.code === 'username_taken' || data.code === 'username_invalid') {
          setShowCarDetailsFields(true);
          setNeedsUsername(true);
          setTempIdToken(idToken);
        }
        alert(`Google login failed: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error during Google login:', error);
      alert('An error occurred during Google login.');
    }
  };

  const handleCarDetailsSubmit = (e) => {
    e.preventDefault();
    if (tempIdToken) {
      handleGoogleSuccess({ credential: tempIdToken });
    }
  };

  // The server answers the same whether or not the address has an account, so this always moves on
  // to the code step and never says which it was.
  const requestResetCode = async (e) => {
    if (e) e.preventDefault();
    const email = resetEmail.trim();
    if (!email.includes('@')) {
      setResetError('Please enter the email address of your account.');
      return;
    }
    setResetBusy(true);
    setResetError('');
    try {
      const response = await fetch('/api/password/forgot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.status === 429) {
        setResetError('Too many attempts. Please try again later.');
        return;
      }
      setResetCode('');
      setResetStep('code');
    } catch (error) {
      setResetError('Could not connect to the server.');
    } finally {
      setResetBusy(false);
    }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(resetCode.replace(/\s/g, ''))) {
      setResetError('Enter the 6-digit code from the email.');
      return;
    }
    const weak = passwordProblem(resetPassword, { email: resetEmail });
    if (weak) {
      setResetError(weak);
      return;
    }
    if (resetPassword !== resetPassword2) {
      setResetError('The two passwords do not match.');
      return;
    }
    setResetBusy(true);
    setResetError('');
    try {
      const response = await fetch('/api/password/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail.trim(), code: resetCode, newPassword: resetPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setResetError(data.message || 'That code is wrong or has expired.');
        return;
      }
      // Sign-in is by username, which is easy to forget along with the password: fill it in.
      if (data.username) setUsername(data.username);
      setPassword('');
      leaveReset();
      setNotice(data.username ? `Password changed. You can now sign in as ${data.username}.` : 'Password changed. You can now sign in.');
    } catch (error) {
      setResetError('Could not connect to the server.');
    } finally {
      setResetBusy(false);
    }
  };

  const leaveReset = () => {
    setResetStep(null);
    setResetCode('');
    setResetPassword('');
    setResetPassword2('');
    setResetError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Use Keycloak's Token Endpoint (Direct Access Grant)
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Save token to localStorage
        setToken(data.token);
        
        // Note: We are no longer manually updating the Keycloak object here
        // as we are using local tokens now.

        sessionStorage.setItem('welcomeMessage', `Welcome, ${username}!`);
        
        // Hard redirect to dashboard
        window.location.href = '/dashboard';
      } else {
        const errorData = await response.json();
        alert(`Login failed: ${errorData.message || 'Invalid username or password'}`);
      }
    } catch (error) {
      console.error('Error during login:', error);
      alert('An error occurred during login.');
    }
  };

  return (
    <>
      <div className="auth-page-wrapper">
        <div className="logo-container">
          {/* A plain <a>, not <Link>: "/" is the static landing page and Caddy only rewrites it on
              a real document request — a router pushState would resolve "/" inside the app. */}
          <a href="/" className="brand-home" aria-label="Back to the Udal home page">
            <img src="/udal-logo-small.png" alt="" className="auth-logo" />
            <h1 className="logo">Udal</h1>
          </a>
          <h2 className="tagline-login">It notices when you <span className="highlight">park</span>. It shares when you <span className="highlight">leave</span>.</h2>
        </div>
        
        <div className="auth-top-background"></div>
        <div className="auth-container">
          <h2>{showCarDetailsFields ? (needsUsername ? 'Complete Your Profile' : 'Enter Car Details') : resetStep ? 'Reset Password' : 'Login'}</h2>
        {resetStep === 'email' ? (
          <form onSubmit={requestResetCode}>
            <p className="reset-hint">Enter the email address of your account and we'll send you a 6-digit code.</p>
            <div className="form-group">
              <label htmlFor="resetEmail">Email</label>
              <input
                type="email"
                id="resetEmail"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            {resetError && <p className="reset-error">{resetError}</p>}
            <button type="submit" disabled={resetBusy}>{resetBusy ? 'Sending…' : 'Send code'}</button>
            <button type="button" className="cancel-button" onClick={leaveReset} style={{ backgroundColor: '#6c757d', marginTop: '10px' }}>Back to login</button>
          </form>
        ) : resetStep === 'code' ? (
          <form onSubmit={confirmReset}>
            <p className="reset-hint">If {resetEmail.trim()} belongs to an account, we've sent it a code. It expires in 15 minutes.</p>
            <div className="form-group">
              <label htmlFor="resetCode">6-digit code</label>
              <input
                type="text"
                id="resetCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="resetPassword">New password</label>
              <PasswordField
                id="resetPassword"
                autoComplete="new-password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                required
              />
              <PasswordChecklist password={resetPassword} email={resetEmail} />
            </div>
            <div className="form-group">
              <label htmlFor="resetPassword2">Repeat new password</label>
              <PasswordField
                id="resetPassword2"
                autoComplete="new-password"
                value={resetPassword2}
                onChange={(e) => setResetPassword2(e.target.value)}
                required
              />
            </div>
            {resetError && <p className="reset-error">{resetError}</p>}
            <button
              type="submit"
              disabled={resetBusy || !checkPassword(resetPassword, { email: resetEmail }).ok}
              style={checkPassword(resetPassword, { email: resetEmail }).ok ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}
            >
              {resetBusy ? 'Changing…' : 'Change password'}
            </button>
            <p className="reset-hint">
              <a href="#resend" onClick={(e) => { e.preventDefault(); if (!resetBusy) requestResetCode(); }}>Send a new code</a>
            </p>
            <button type="button" className="cancel-button" onClick={leaveReset} style={{ backgroundColor: '#6c757d', marginTop: '10px' }}>Back to login</button>
          </form>
        ) : showCarDetailsFields ? (
          <form onSubmit={handleCarDetailsSubmit}>
            {needsUsername && (
              <div className="form-group">
                <label htmlFor="googleUsername">Username <span className="required-mark">*</span></label>
                <input
                  type="text"
                  id="googleUsername"
                  value={googleUsername}
                  onChange={(e) => setGoogleUsername(e.target.value)}
                  autoComplete="off"
                  required
                />
              </div>
            )}
            <div className="form-group">
              <label htmlFor="plateNumber">Plate Number <span className="required-mark">*</span></label>
              <input
                type="text"
                id="plateNumber"
                value={plateNumber}
                onChange={(e) => setPlateNumber(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="carColor">Car Color <span className="required-mark">*</span></label>
              <input
                type="text"
                id="carColor"
                value={carColor}
                onChange={(e) => setCarColor(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="carType">Car size <span className="required-mark">*</span></label>
              <select id="carType" value={carType} onChange={(e) => setCarType(e.target.value)} required>
                <CarSizeOptions />
              </select>
            </div>
            <button type="submit">Complete Google Login</button>
            <button type="button" className="cancel-button" onClick={() => setShowCarDetailsFields(false)} style={{ backgroundColor: '#6c757d', marginTop: '10px' }}>Cancel</button>
          </form>
        ) : (
          <>
            {notice && <p className="auth-notice">{notice}</p>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username">Username:</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <PasswordField
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <p className="forgot-link">
                <a href="#forgot" onClick={(e) => { e.preventDefault(); setNotice(''); setResetError(''); setResetStep('email'); }}>Forgot password?</a>
              </p>
              <button type="submit">Login</button>
              <div className="social-login-separator">
                <span>OR</span>
              </div>
              <div className="google-login-container">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => {
                    console.log('Login Failed');
                    alert('Google Login Failed');
                  }}
                  useOneTap
                />
              </div>
            </form>
          </>
        )}
        <p>
          Don't have an account? <Link to="/register">Register here</Link>
        </p>
        <p className="back-home"><a href="/">&larr; Back to home</a></p>
      </div>
    <footer className="App-footer">
        <p>&copy; 2026 UDAL LIMITED</p>
      </footer>
    </div>
    </>
  );
};

export default Login;
